import { useEffect, useState } from "react"
import { useAppDispatch } from "../../reduxStore/hooks"
import { useSelector } from "react-redux"
import {
  AuthStatus,
  selectAccountGuid,
  selectAuthenticationStatus,
} from "../authentication/authenticationSlice"
import { selectValidatedHealthAdvocateUser } from "../shared/selectors"
import {
  selectStatus as getTouStatus,
  TOUStatusEnum,
} from "../termsOfUse/termsOfUseSlice"
import { getTaxInfoTablesData } from "../../domain/Practice/loaders/taxInformation"
import {
  fetchBaseProvider,
  getProviderNameThunk,
  getProviderPracticeNamesThunk,
} from "../../domain/Provider/loader"
import { DataStatus } from "../../domain/enums"
import {
  getAllPartialPractices,
  getPracticeInvitesForUser,
} from "../../domain/Practices/loader"
import { getPracticeNamesData } from "../practiceProfile/practiceProfileProcessSlice"
import { PracticeName } from "../../domain/Practice/loaders/loader"
import { isArray, isEmpty, map } from "lodash"
import { ProviderNameResult } from "../../domain/Provider/schema"
import {
  selectUserDataStatus,
  getSiteOutageMessage,
} from "../../domain/Account/userSlice"
import { selectUser } from "../shared/selectors"
import { dataStatusesLoading } from "../shared/utils"
import { getPracticeCompletenessData } from "../../domain/Practice/loaders/practiceCompleteness"
import { autoSelectAffiliatedOrInvitePracticeId } from "../../domain/Practices/helpers"
import { addOrUpdateBannerById, BannerTypeEnum } from "../errors/errorSlice"
import { BannerIds } from "../errors/components/BannerWithId"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { FeatureFlag } from "../../domain/enums/featureFlags"
import { useCMSReady } from "../../domain/CMS/useCMSReady"

export const useInitialDataFetch = (): DataStatus => {
  const dispatch = useAppDispatch()
  const userId = useSelector(selectAccountGuid)
  const user = useSelector(selectUser)
  const userDataStatus = useSelector(selectUserDataStatus)
  const authStatus = useSelector(selectAuthenticationStatus)
  const isHaUser = useSelector(selectValidatedHealthAdvocateUser)
  const touStatus = useSelector(getTouStatus)
  const [initialDataFetchDataStatus, setInitialDataFetchDataStatus] =
    useState<DataStatus>(DataStatus.LOADING)

  const userAuthenticated = authStatus === AuthStatus.Authenticated

  const isSystemAlertBlueColorEnabled = !!useFeatureFlagEnabled(
    FeatureFlag.IsSystemAlertBlueColor21381Enabled,
  )

  const {
    cms: { common: commonText },
  } = useCMSReady(["common"])

  const touAccepted = touStatus === TOUStatusEnum.ACCEPTED
  const userFetchComplete = !dataStatusesLoading([
    userDataStatus.userRecordDataStatus,
  ])

  // we need to initially fetch the following data to be able to proceed into the app:
  // 0) user is already fetched from msal initialization (which includes tou) - dont include here
  // 1) taxInfoTables (we dont really NEED this... its carrying over from the previous initial data fetch for now)
  // 2) practice names (non ha)
  // 3) provider name (non ha)
  // 4) all fullPractices (non ha)
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        if (userId && touAccepted) {
          let practiceNames: PracticeName[] = []
          let providerName: ProviderNameResult | undefined

          setInitialDataFetchDataStatus(DataStatus.SENDING)
          dispatch(getTaxInfoTablesData())

          const siteOutageAction = await dispatch(getSiteOutageMessage())
          const outageMessage = siteOutageAction.payload as {
            message?: string
            isEnabled?: boolean
          } | null
          if (outageMessage?.isEnabled && outageMessage?.message) {
            dispatch(
              addOrUpdateBannerById({
                id: BannerIds.SITE_OUTAGE.error,
                type: isSystemAlertBlueColorEnabled
                  ? BannerTypeEnum.INFO
                  : BannerTypeEnum.ERROR,
                title: commonText?.alert ?? "Alert",
                content: outageMessage.message,
              }),
            )
          }

          if (!isHaUser) {
            await dispatch(getProviderPracticeNamesThunk({ userId }))
            const practiceInvites = await dispatch(
              getPracticeInvitesForUser({ userId }),
            ).unwrap()

            const practiceNamesAction = await dispatch(getPracticeNamesData())
            practiceNames = practiceNamesAction.payload as PracticeName[]
            const providerNameAction = await dispatch(getProviderNameThunk())
            providerName = providerNameAction.payload as ProviderNameResult

            if (!!providerName && !!providerName.id) {
              await dispatch(
                fetchBaseProvider({
                  providerId: providerName.id,
                }),
              )
            }
            if (practiceNames.length > 0) {
              const practiceIds = map(practiceNames, "id")
              const practices = await dispatch(
                getAllPartialPractices({ practiceIds }),
              ).unwrap()

              const hasPractices = practices && !isEmpty(practices)
              const hasPracticeInvites = isArray(practiceInvites)
              if (hasPractices && hasPracticeInvites) {
                const autoSelectedPracticeIdResult =
                  autoSelectAffiliatedOrInvitePracticeId(
                    practices,
                    practiceInvites,
                  )
                if (autoSelectedPracticeIdResult) {
                  await dispatch(
                    getPracticeCompletenessData(
                      autoSelectedPracticeIdResult.practiceId,
                    ),
                  )
                }
              }
            }
          }

          setInitialDataFetchDataStatus(DataStatus.LOADED)
        }

        // this is the case where the user has not made their account record yet (pre-tou sign)
        // we dont want to fetch anything but still allow the user into the app
        if (userId && userFetchComplete && !touAccepted) {
          setInitialDataFetchDataStatus(DataStatus.LOADED)
        }
      } catch (error) {
        setInitialDataFetchDataStatus(DataStatus.ERROR)
        console.error("Error fetching initial data:", error)
      }
    }

    if (
      initialDataFetchDataStatus === DataStatus.LOADING &&
      userFetchComplete
    ) {
      fetchInitialData()
    }
  }, [
    dispatch,
    initialDataFetchDataStatus,
    isHaUser,
    isSystemAlertBlueColorEnabled,
    touAccepted,
    touStatus,
    userAuthenticated,
    userFetchComplete,
    userId,
    user,
    commonText?.alert,
  ])

  return initialDataFetchDataStatus
}
